import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"
import SelectContacts from "../helpers/select_contacts"

// Loose on purpose: the backend (validates_email_format_of) stays the authority on submit, this
// only decides whether a row counts toward the "Send to N Recipients" button.
const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Mirrors CompletedRecipientValidator#prevent_gifting_in_the_past's 30 minute grace period.
const PAST_SEND_GRACE_MS = 30 * 60 * 1000;

// The recipient step of the give flow. Mounted on .recipients, which also contains the
// #gift-csv-upload modal owned by recipient_upload_controller.js.
export default class extends Controller {
  static get classes() {
    return ["skipDuplicateValidation"];
  }

  static get targets() {
    return ["sendButtonLabel"];
  }

  connect() {
    // Kept jQuery-wrapped rather than retreated: updateButtonState feeds these lookups into
    // Bootstrap's .tooltip() plugin, which makes a full retreat a larger, riskier change than
    // this pass covers.
    this.$element = $(this.element);

    this.addPath = this.$element.find("#giving-form").data("addPath");
    this.recipientCount = this.$element.data("recipients");
    this.maxRecipients = this.$element.data("maxrecipients");
    this.customPackaging = this.$element.data("custom-packaging");

    this.dateTimePickerConfig = {
      ...window.datetimepickerDefaults,
      showClear: true,
      toolbarPlacement: "top"
    };

    this.buttons = [
      this.$element.find("#add-recipient-btn"),
      this.$element.find("#add-sms-recipient-btn"),
      this.$element.find("#import-recipient-btn"),
      this.$element.find("#add-from-contacts-btn"),
    ];

    // bound for a jQuery plugin event ("dp.show", fired by the datetimepicker plugin, not a
    // native DOM event) -- data-action can't see it, so this stays a manual handler.
    this.customizeClearButton = this.customizeClearButton.bind(this);
    this.updateSendButton = this.updateSendButton.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.element.addEventListener("click", this.onNextClick);

    this.updateButtonState();
    this.bindDateTimePicker(this.$element.find(".scheduled-at-picker"));
    this.updateSendButton();

    if (this.$element.find("#select-contacts-modal").length > 0) {
      this.selectContacts = new SelectContacts(this.$element.find("#select-contacts-modal"), {
        maxRecipients: this.maxRecipients,
        existingContactsCountFn: () => this.recipientCount,
        addSelectedContactsCallback: (recipientsHtml, count) => {
          this.replaceGivingForm(recipientsHtml);
          this.incrementRecipientCount(count);
          this.updateButtonState();
        }
      });
    }
  }

  disconnect() {
    this.element.removeEventListener("click", this.onNextClick);
  }

  bindDateTimePicker($fields) {
    $fields.datetimepicker(this.dateTimePickerConfig);
    $fields.on("dp.show", this.customizeClearButton);
    // "dp.change" is the plugin's own jQuery event, so it can't be wired with data-action either.
    $fields.on("dp.change", this.updateSendButton);
  }

  // Labels the "Send to N Recipients" button with the rows that would pass the backend's
  // completed-recipient validation. Informational only: the button stays clickable so the
  // backend can report what's wrong with any row that doesn't count.
  updateSendButton() {
    if (!this.hasSendButtonLabelTarget) { return; }

    const count = Array.from(this.element.querySelectorAll(".build-recipient-row"))
      .filter((row) => this.isValidRecipientRow(row)).length;

    this.sendButtonLabelTarget.textContent = `Send to ${count} ${count === 1 ? "Recipient" : "Recipients"}`;
  }

  // Bootstrap's dropdown only handles Escape on its toggle or a .dropdown-menu, and the mobile
  // actions menu is neither, so Escape from inside it closes it here and returns focus to the
  // toggle. Inline from tablet up, where there is nothing to close.
  closeActionsMenu(event) {
    const group = event.currentTarget.closest(".build-recipient-buttons-group");
    if (this.toggleOpenActionsMenu(group)) {
      group.querySelector(".build-recipient-buttons-toggle").focus();
    }
  }

  // Tabbing past the last item would otherwise leave the menu open over the recipient inputs.
  closeActionsMenuOnFocusOut(event) {
    const group = event.currentTarget;
    if (group.contains(event.relatedTarget)) { return; }

    this.toggleOpenActionsMenu(group);
  }

  // Returns whether the menu was open (and is now closed).
  toggleOpenActionsMenu(group) {
    if (!group.classList.contains("open")) { return false; }

    // .dropdown() is a Bootstrap plugin call -- stays on jQuery (R8 allowlist).
    $(group.querySelector(".build-recipient-buttons-toggle")).dropdown("toggle");
    return true;
  }

  isValidRecipientRow(row) {
    const name = row.querySelector("input[id$='name']");
    if (!name || name.value.trim() === "") { return false; }

    const email = row.querySelector("input[id$='email']");
    if (email && !EMAIL_FORMAT.test(email.value.trim())) { return false; }

    const phone = row.querySelector("input[id$='phone_number']");
    if (phone && phone.value.trim() === "") { return false; }

    return !this.isScheduledInThePast(row);
  }

  // The picker's own parsed value (a moment) rather than re-parsing the "MM/DD/YYYY hh:mmA z"
  // string. A blank picker means "send immediately", which is always valid.
  isScheduledInThePast(row) {
    const picker = $(row).find(".scheduled-at-picker").data("DateTimePicker");
    const scheduledAt = picker && picker.date();
    return !!scheduledAt && scheduledAt.valueOf() < Date.now() - PAST_SEND_GRACE_MS;
  }

  // The widget is rendered by the datetimepicker plugin itself (appended outside this.element,
  // on demand, at an unpredictable time) and only exists while it's open -- a document-global
  // lookup is the only option, not a Target. Not exercised by give_recipient_spec.rb; left on
  // jQuery and unconverted rather than claim a neuter-red proof this pass doesn't have.
  customizeClearButton() {
    const widget = $(".bootstrap-datetimepicker-widget");
    widget.find("a[data-action='clear']").addClass("btn btn-borderless").html("Send Immediately");
  }

  updateButtonState() {
    if (this.recipientCount >= this.maxRecipients) {
      this.buttons.forEach(($button) => {
        $button.addClass("btn-disabled");
        if ($button.hasClass("custom-packaging-disabled") && this.customPackaging) {
          $button.parents(".tooltip-wrapper").tooltip({
            title: $button.data("custom-packaging-title"),
            placement: "bottom",
            trigger: "hover"
          });
        } else {
          $button.parents(".tooltip-wrapper").tooltip({
            title: $button.data("max-recipients-title"),
            placement: "bottom",
            trigger: "hover"
          });
        }
      });
    } else {
      this.buttons.forEach(($button) => {
        $button.removeClass("btn-disabled");
        $button.parents(".tooltip-wrapper").tooltip("destroy");
      });
    }
  }

  incrementRecipientCount(count = 1) {
    this.recipientCount += count;
  }

  decrementRecipientCount() {
    this.recipientCount -= 1;
  }

  // No plugin involved on this element -- plain DOM, converted off jQuery. querySelectorAll
  // (not a single querySelector + ?.) so a row that somehow carries more than one .help-block
  // still gets them all cleared, matching jQuery's all-matches .remove() (pattern 1).
  // "has-error" itself stays a hard-coded literal rather than moving to the Classes API: it's
  // Bootstrap's own form-validation-state class (bootstrap-yeti.scss), reused app-wide (39 files),
  // not something this controller owns (D1).
  removeErrorMessages(input) {
    input.closest(".build_build_recipients_email").classList.remove("has-error");
    input.closest(".input-addon-group").querySelectorAll(".help-block").forEach((el) => el.remove());
  }

  // Builds the error message as DOM nodes rather than an HTML string -- no parsing, so this
  // can't reach S1/S4 no matter what the (currently static) text becomes later.
  addErrorMessages(input) {
    input.closest(".build_build_recipients_email").classList.add("has-error");

    const message = document.createElement("span");
    message.className = "help-block";
    const icon = document.createElement("i");
    icon.className = "icon-lasso-info";
    message.append(icon, "Duplicated Email");

    input.parentElement.after(message);
  }

  resetDuplicatesModal(modal) {
    modal.querySelector(".number-of-emails").textContent = "";
    modal.querySelector(".email-list").replaceChildren();
  }

  validateDuplicates() {
    const emailsList = new Map(), duplicateList = new Set();
    // ":input" is a jQuery extension (not valid CSS) that matches input/select/textarea/button --
    // replicated explicitly so this stays a plain querySelectorAll.
    // Not converted to a Target: .build_build_recipients_email is a simple_form wrapper class
    // rendered per-row by _recipients_form.html.haml, which replaceGivingForm below replaces
    // wholesale on every add/remove. Declaring a target here would need a markup change to that
    // partial's wrapper_html, deferred rather than made on this pass's own supposition of how
    // simple_form's wrapper options compose (D2/pattern 20: accepted as the R8-to-R2 trade).
    const emailInputs = this.element.querySelectorAll(
      ".build_build_recipients_email input, .build_build_recipients_email select, " +
      ".build_build_recipients_email textarea, .build_build_recipients_email button"
    );

    emailInputs.forEach((input) => {
      this.removeErrorMessages(input);

      const email = input.value.trim().toLowerCase();
      if (emailsList.has(email)) {
        duplicateList.add(email);
        this.addErrorMessages(input);
      } else {
        emailsList.set(email, input.id);
      }
    });

    if (duplicateList.size > 0) {
      const modal = document.getElementById("duplicate_recipient_modal");
      this.resetDuplicatesModal(modal);
      modal.querySelector(".number-of-emails").append(String(duplicateList.size));
      const emailList = modal.querySelector(".email-list");
      duplicateList.forEach((email) => {
        this.addErrorMessages(this.element.querySelector("#" + emailsList.get(email)));

        const listItem = document.createElement("li");
        const link = document.createElement("a");
        link.textContent = email;
        listItem.append(link);
        emailList.append(listItem);
      });

      // .modal() is a Bootstrap plugin call -- stays on jQuery (R8 allowlist).
      $(modal).modal("show");
      return false;
    }

    return true;
  }

  // Delegated from the root rather than wired with a data-action: the desktop "Next" link lives
  // in gives/_progress.html.haml, shared by every give step, so it can't carry this step's own
  // action. One handler also covers the "Send Anyway" button, the mobile #next duplicate and the
  // module's "Send to N Recipients" button, which all submit the same way.
  onNextClick(event) {
    const nextButton = event.target.closest("#next, .skip-duplicate-validation, .send-to-recipients");
    if (!nextButton) { return; }

    // .btn-disabled only blocks pointer clicks; keyboard activation of the link still lands here.
    if (nextButton.classList.contains("btn-disabled")) {
      event.preventDefault();
      return;
    }

    // "Next" does what the selected module's button does; only individual gifts submit this form
    const selectedModule = this.element.querySelector(".sending-method.selected");
    if (nextButton.id === "next" && selectedModule && selectedModule.dataset.method !== "individual") {
      event.preventDefault();
      selectedModule.querySelector("[data-delivery-method-cta]")?.click();
      return;
    }

    this.nextStep(event, nextButton);
  }

  // For Next controls that carry their own data-action, such as the ones Send a Gift renders.
  next(event) {
    this.nextStep(event, event.currentTarget);
  }

  nextStep(event, nextButton) {
    event.preventDefault();

    // hasSkipDuplicateValidationClass is always false absent a data-give-recipient-skip-
    // duplicate-validation-class override in the markup, which nothing declares -- that's
    // Stimulus's own Classes API fallback, not a guess (pattern 20).
    const skipClass = this.hasSkipDuplicateValidationClass ? this.skipDuplicateValidationClass : "skip-duplicate-validation";

    if (nextButton.classList.contains(skipClass)) {
      this.$element.find("form#giving-form").submit();
    } else if (this.validateDuplicates()) {
      this.$element.find("form#giving-form").submit();
    }
  }

  addRecipientRow() {
    fetch(this.addPath, RequestHelper.defaultRequestOptions())
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          $("#custom-packaging-out-of-stock").modal("show");
          console.warn(data);
          return;
        }

        this.replaceGivingForm(data.html);
        this.incrementRecipientCount();
        this.updateButtonState();
      });
  }

  addSMSRecipientRow() {
    fetch(`${this.addPath}?delivery_method=sms`, RequestHelper.defaultRequestOptions())
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          console.warn(data);
          return;
        }

        this.replaceGivingForm(data.html);
        this.incrementRecipientCount();
        this.updateButtonState();
      });
  }

  writeBuildRecipient(event) {
    if (event.view === undefined) {
      // Ignore calls from duplicate blur events made by datetimepicker that aren't on Window
      return;
    }

    const row = event.target.closest(".build-recipient-row");
    const path = row.dataset.path;
    const buildRecipientParams = {
      name: row.querySelector("input[id$='name']").value,
      scheduled_at: row.querySelector("input[id$='scheduled_at']").value
    };
    const emailInput = row.querySelector("input[id$='email']");
    if (emailInput) {
      buildRecipientParams["email"] = emailInput.value;
    }
    const phoneInput = row.querySelector("input[id$='phone_number']");
    if (phoneInput) {
      buildRecipientParams["phone_number"] = phoneInput.value;
    }

    const options = RequestHelper.defaultPatchOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify({ build_recipient: buildRecipientParams });

    fetch(path, options).then((response) => {
      if (!response.ok) { response.json().then((data) => console.warn(data)); }
    });
  }

  destroyRecipientRow(event) {
    const row = event.target.closest(".build-recipient-row");
    const path = row.dataset.path;

    fetch(path, RequestHelper.defaultDeleteOptions())
      .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) {
          console.warn(data);
          return;
        }

        this.replaceGivingForm(data.html);
        this.decrementRecipientCount();
        this.updateButtonState();
      });
  }

  // Shared by addRecipientRow/addSMSRecipientRow/destroyRecipientRow and the select-contacts
  // add-selected callback. Stimulus connects anything in the inserted markup on its own; only the
  // datetimepicker needs rebinding, scoped to the fresh fragment.
  // html is server-rendered by this same controller's own Gives::RecipientsController /
  // Gives::BuildRecipientsController JSON actions (our own origin). Kept on jQuery rather than a
  // native parse: .find() below needs a query-able collection before insertion, and jQuery's own
  // HTML-string handling executes embedded <script> the way ours already relies on, which a native
  // replaceWith(String) does not (D4).
  replaceGivingForm(html) {
    const $newForm = $(html);
    this.bindDateTimePicker($newForm.find(".scheduled-at-picker"));
    this.$element.find("#giving-form").replaceWith($newForm);
    this.updateSendButton();
  }
}
